import type * as React from 'react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@connectrpc/connect-query'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import type { Customer } from '@jotta/grpc-connect-openapi/customer'
import { getPaymentInfo } from '@jotta/grpc-connect-openapi/subscriptionQuery'
import { Avatar } from '@jotta/ui/Avatar'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { Popover, PopoverContent, PopoverTrigger } from '@jotta/ui/Popover'
import { Skeleton } from '@jotta/ui/Skeleton'
import SvgInstall from '@jotta/ui/SvgInstall'
import SvgSettings from '@jotta/ui/SvgSettings'
import SvgSignOut from '@jotta/ui/SvgSignOut'
import { getBrandZIndex } from '@jotta/ui/zIndex'
import { redirectToLogout } from '@jotta/utils/auth'
import { cn } from '@jotta/utils/css'
import { formatBytes } from '@jotta/utils/formatStorage'
import { isSubscriptionHidden } from '@jotta/utils/isSubscriptionHidden'

import { LinearProgress } from '../../Elements/ProgressIndicator/Linear/LinearProgress/LinearProgress'

export type UserMenuProps = {
  customer: Customer
  defaultOpen?: boolean
}

export const UserMenu = ({ customer, defaultOpen = false }: UserMenuProps) => {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="btn btn-ghost p-0"
          aria-label={t`Show user menu`}
          data-testid="UserMenuAvatar"
        >
          <Avatar
            className={cn({
              'border-on-primary-container outline outline-2': open,
            })}
            avatar={customer.avatar}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="flex max-h-full flex-col gap-2 rounded-2xl bg-surface-container-low p-3 md:max-w-lg"
        style={{ zIndex: getBrandZIndex('appHeaderAvatar') }}
        hasArrow={false}
      >
        <CustomerInfo customer={customer} />
        <SubscriptionInfo customer={customer} />
        <UserMenuActions onClick={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  )
}

export function CustomerInfo({
  customer,
  className,
}: React.ComponentProps<'div'> & { customer: Customer }) {
  return (
    <div
      className={cn(
        'flex flex-row items-center gap-4 rounded-2xl bg-on-primary p-3',
        className,
      )}
    >
      <Avatar className="size-14" avatar={customer.avatar} />
      <div className="flex w-full min-w-0 flex-col">
        <span className="truncate text-lg font-semibold">
          {customer.fullName || customer.email}
        </span>
        <span className="truncate">{customer.email}</span>
      </div>
    </div>
  )
}

export function SubscriptionInfo({
  customer,
  className,
}: React.ComponentProps<'div'> & { customer: Customer }) {
  const navigate = useNavigate()
  const { data: subscription } = useQuery(getPaymentInfo, undefined)

  const { quotaBytes, promoteUpgrade, usageBytes } = customer
  const usageBytesNumber = Number(usageBytes)
  const quotaBytesNumber = Number(quotaBytes)
  const isUnlimited = quotaBytesNumber < 0
  const isNearQuota =
    !isUnlimited &&
    quotaBytesNumber > 0 &&
    usageBytesNumber / quotaBytesNumber > 0.9

  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-2xl bg-on-primary p-3',
        className,
      )}
    >
      {subscription?.subscriptionNameLocalized || (
        <Skeleton className="w-1/2" />
      )}
      {!isUnlimited && (
        <LinearProgress
          completed={usageBytesNumber}
          total={quotaBytesNumber}
          variant={isNearQuota ? 'danger' : 'black'}
        />
      )}
      <span>
        {isUnlimited ? (
          <>
            <strong>{formatBytes(usageBytesNumber)}</strong> <Trans>used</Trans>
          </>
        ) : (
          <>
            <strong>{formatBytes(usageBytesNumber)}</strong> <Trans>of</Trans>{' '}
            {formatBytes(quotaBytesNumber)} <Trans>used</Trans>
          </>
        )}
      </span>
      {promoteUpgrade && !isSubscriptionHidden() && (
        <div className="flex justify-end">
          <button
            className="btn btn-tonal"
            data-testid="UserMenuUpgrade"
            onClick={() => navigate('/web/upgrade')}
          >
            <Trans>Change subscription</Trans>
          </button>
        </div>
      )}
    </div>
  )
}

export function UserMenuActions({
  onClick,
  className,
}: React.ComponentProps<'div'> & { onClick?: () => void }) {
  const navigate = useNavigate()

  return (
    <div className={cn('flex flex-wrap justify-end gap-3', className)}>
      <button
        className="btn btn-elevated hidden lg:flex"
        data-testid="UserMenuDownload"
        onClick={() => {
          navigate('/download')
        }}
      >
        <PlainBrandIcon icon={SvgInstall} />
        <Trans>Install</Trans>
      </button>
      <button
        className="btn btn-elevated"
        data-testid="UserMenuSettings"
        onClick={() => {
          onClick?.()
          navigate('/web/account')
        }}
      >
        <PlainBrandIcon icon={SvgSettings} />
        <Trans>Settings</Trans>
      </button>
      <button
        className="btn btn-elevated"
        data-testid="UserMenuLogout"
        onClick={() => redirectToLogout(`${window.location.origin}/web/sync`)}
      >
        <PlainBrandIcon icon={SvgSignOut} />
        <Trans>Log out</Trans>
      </button>
    </div>
  )
}
